// ADIRATH — Certifications, Careers, Client Portal pages.
import React from 'react';
import { Button } from './components/buttons/Button';
import { Badge } from './components/data-display/Badge';
import { Input } from './components/forms/Input';
import { IMG } from './images';
import { Reveal, SectionHead } from './primitives';
import { PageHero } from './serviceData';
import { Icon } from './Icon';
import { CTASection } from './HomeSectionsB';

export function CertificationsPage({ onNavigate }) {
  const base = '/certs/';
  const groups = [
    ['Quality, Environment & Safety', 'shield-check', [
      [base + 'iso-9001.webp', 'ISO 9001:2015', 'Quality Management System — TÜV SÜD'],
      [base + 'iso-14001.webp', 'ISO 14001:2015', 'Environmental Management System — TÜV SÜD'],
      [base + 'iso-45001.webp', 'ISO 45001:2018', 'Occupational Health & Safety — TÜV SÜD'],
    ]],
    ['Product Compliance', 'badge-check', [
      [base + 'ce.webp', 'CE — LVD 2014/35/EU', 'Certificate of Compliance — Quality Veritas'],
      [base + 'rohs.webp', 'RoHS (2015/863)', 'Restriction of Hazardous Substances — Quality Veritas'],
      [base + 'zed-bronze.webp', 'MSME ZED Bronze', 'Zero Defect Zero Effect — Ministry of MSME'],
    ]],
    ['Government Recognition', 'landmark', [
      [base + 'startup-india.webp', 'Startup India', 'DPIIT Recognition — DIPP78128'],
      [base + 'msme-udyam.webp', 'Udyam (MSME)', 'UDYAM-TN-11-0004434'],
      [base + 'cii-membership.webp', 'CII Member', 'Confederation of Indian Industry'],
    ]],
  ];
  const flat = groups.flatMap((g) => g[2]);
  const [light, setLight] = React.useState(null);

  React.useEffect(() => {
    const onKey = (e) => {
      if (light === null) return;
      if (e.key === 'Escape') setLight(null);
      if (e.key === 'ArrowRight') setLight((l) => (l + 1) % flat.length);
      if (e.key === 'ArrowLeft') setLight((l) => (l - 1 + flat.length) % flat.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [light, flat.length]);

  return (
    <div>
      <PageHero kicker="Certificates" crumbs={['Certificates']} onNavigate={onNavigate} image="co-facility"
        title="Certified, compliant, audited — trust engineered in."
        sub="EEE Energy Private Limited (ADIRATH) is certified to recognised international and Indian standards. View any certificate below." />

      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          {groups.map(([title, ic, items], gi) => (
            <div key={title} style={{ marginBottom: gi < groups.length - 1 ? 'var(--space-8)' : 0 }}>
              <Reveal variant="up" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 26 }}>
                <span style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--energy-soft)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={ic} style={{ width: 23, height: 23 }} /></span>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h3)', color: 'var(--text-strong)', margin: 0 }}>{title}</h2>
              </Reveal>
              <div style={{ display: 'grid', gap: 20 }} className="adr-cert-grid">
                {items.map(([src, name, desc]) => {
                  const idx = flat.findIndex((f) => f[0] === src);
                  return (
                    <Reveal key={src} variant="up" delay={0.04}>
                      <CertCard src={src} name={name} desc={desc} onOpen={() => setLight(idx)} />
                    </Reveal>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {light !== null && flat[light] && (
        <div onClick={() => setLight(null)} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(5,15,31,0.93)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--gutter)' }}>
          <button aria-label="Close" onClick={() => setLight(null)} style={certLbBtn({ top: 22, right: 22 })}><Icon name="x" style={{ width: 24, height: 24 }} /></button>
          <button aria-label="Previous" onClick={(e) => { e.stopPropagation(); setLight((l) => (l - 1 + flat.length) % flat.length); }} style={certLbBtn({ left: 22 })}><Icon name="chevron-left" style={{ width: 26, height: 26 }} /></button>
          <button aria-label="Next" onClick={(e) => { e.stopPropagation(); setLight((l) => (l + 1) % flat.length); }} style={certLbBtn({ right: 22 })}><Icon name="chevron-right" style={{ width: 26, height: 26 }} /></button>
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: '90vh' }}>
            <img src={flat[light][0]} alt={flat[light][1]} style={{ maxWidth: 'min(680px, 92vw)', maxHeight: '78vh', objectFit: 'contain', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-xl)', background: '#fff' }} />
            <div style={{ marginTop: 14, textAlign: 'center', color: '#fff' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 18 }}>{flat[light][1]}</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginTop: 2 }}>{flat[light][2]}</div>
            </div>
          </div>
        </div>
      )}

      <CTASection onNavigate={onNavigate} />
    </div>
  );
}

export function CertCard({ src, name, desc, onOpen }) {
  const [h, setH] = React.useState(false);
  return (
    <div onClick={onOpen} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      cursor: 'pointer', background: 'var(--surface-card)', border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: h ? 'var(--glow-blue)' : 'var(--shadow-sm)',
      transform: h ? 'translateY(-4px)' : 'none', transition: 'all var(--dur-med) var(--ease-out)',
    }}>
      <div style={{ position: 'relative', height: 220, background: 'var(--surface-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(8,27,51,0) 60%, rgba(8,27,51,0.35))', opacity: h ? 1 : 0, transition: 'opacity var(--dur-fast)' }} />
        <span style={{ position: 'absolute', top: 12, right: 12, width: 34, height: 34, borderRadius: '50%', background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', opacity: h ? 1 : 0, transition: 'opacity var(--dur-fast)' }}><Icon name="maximize-2" style={{ width: 15, height: 15 }} /></span>
      </div>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 15, color: 'var(--text-strong)' }}>{name}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 }}>{desc}</div>
      </div>
    </div>
  );
}

export function certLbBtn(pos) {
  return Object.assign({ position: 'fixed', top: pos.top || '50%', transform: pos.top ? 'none' : 'translateY(-50%)', zIndex: 101, width: 50, height: 50, borderRadius: '50%', border: '1px solid var(--glass-dark-border)', background: 'var(--glass-dark-bg)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }, pos);
}

export function CareersPage({ onNavigate }) {
  const benefits = [
    ['heart-pulse', 'Health & Wellness',
      'Your well-being matters. We provide comprehensive health benefits and a supportive workplace to help you and your family stay healthy, motivated, and focused.'],
    ['graduation-cap', 'Learning & Skill Development',
      <>Stay ahead in the rapidly evolving world of <strong>Battery Technology</strong>, <strong>Battery Energy Storage Systems (BESS)</strong>, <strong>Industrial Automation</strong>, and <strong>Robotics</strong> through technical training, certifications, workshops, and continuous learning opportunities.</>],
    ['trending-up', 'Career Growth & Leadership',
      "Whether you're an engineer, designer, technician, or project professional, we offer clear career progression, hands-on experience, and opportunities to grow into technical and leadership roles."],
    ['scale', 'Work-Life Balance',
      'We believe great ideas come from balanced lives. Our employee-friendly policies promote flexibility, collaboration, and a positive work environment that supports both personal and professional success.'],
    ['coins', 'Recognition & Performance Rewards',
      'Hard work deserves recognition. We celebrate innovation, outstanding performance, teamwork, and contributions that drive customer success and technological excellence.'],
    ['users', 'Collaborative & Innovative Culture',
      <>Work alongside experienced professionals who are passionate about developing <strong>Advanced Lithium Battery Solutions</strong>, <strong>BESS</strong>, <strong>Industrial Mobility Batteries</strong>, and <strong>Smart Automation Systems</strong>. Every project is an opportunity to learn, innovate, and create technologies that power a sustainable future.</>],
  ];
  const jobs = [];
  return (
    <div>
      <PageHero kicker="Careers" crumbs={['Careers']} onNavigate={onNavigate} image="g5"
        title="Build the systems that power a cleaner future."
        sub="Join a team of engineers solving real problems in energy, robotics and automation." />

      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', display: 'grid', gap: 56, alignItems: 'stretch' }} className="adr-split">
          <Reveal variant="scale" style={{ position: 'relative', minHeight: 420 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: 'var(--radius-2xl)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("${IMG('careers-section')}")`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(18px) brightness(0.55)', transform: 'scale(1.12)' }} />
              <img src={IMG('careers-section')} alt="Life at ADIRATH" style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} />
            </div>
          </Reveal>
          <div>
            <SectionHead eyebrow="Life at ADIRATH" title="Innovate. Engineer. Empower." />
            <p style={{ color: 'var(--text-body)', lineHeight: 1.75, margin: '20px 0 16px', fontSize: 'var(--fs-body)' }}>
              At <strong>EEE Energy Private Limited</strong>, we believe great innovations begin with passionate people. We foster a collaborative environment where engineers, designers, technicians, and innovators work together to develop advanced <strong>Battery Energy Storage Systems (BESS)</strong>, <strong>Customized Lithium Battery Packs</strong>, <strong>Industrial Mobility Batteries</strong>, and <strong>Automation Solutions</strong> that power the industries of tomorrow.
            </p>
            <p style={{ color: 'var(--text-body)', lineHeight: 1.75, margin: 0, fontSize: 'var(--fs-body)' }}>
              From research and product development to precision manufacturing and customer success, every team member plays a vital role in shaping sustainable energy solutions and driving technological excellence.
            </p>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <SectionHead dark align="center" eyebrow="Benefits" title="Why Work at ADIRATH?" sub="We Invest in People Who Build the Future" />
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.72)', fontSize: 'var(--fs-body)', lineHeight: 1.75, maxWidth: 680, margin: '16px auto 0' }}>
            At <strong style={{ color: '#fff' }}>EEE Energy Private Limited</strong>, we believe our greatest strength is our people. We create an environment where innovation, continuous learning, and career growth go hand in hand, empowering every team member to make a meaningful impact.
          </p>
          <div className="adr-careers-grid" style={{ display: 'grid', gap: 20, marginTop: 48 }}>
            {benefits.map(([ic, t, d], i) => (
              <Reveal key={t} variant="up" delay={(i % 2) * 0.06}>
                <div style={{
                  display: 'flex', gap: 20, alignItems: 'flex-start',
                  background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)',
                  borderRadius: 'var(--radius-xl)', padding: '28px 32px',
                  height: '100%',
                }}>
                  <span style={{
                    flexShrink: 0, width: 52, height: 52, borderRadius: 14,
                    background: 'rgba(67,196,67,0.15)', color: 'var(--energy-bright)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid rgba(67,196,67,0.2)',
                  }}>
                    <Icon name={ic} style={{ width: 24, height: 24 }} />
                  </span>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.35rem', color: 'var(--energy-bright)', margin: '0 0 10px', lineHeight: 1.3 }}>{t}</h3>
                    <p style={{ margin: 0, color: '#fff', fontSize: '0.975rem', lineHeight: 1.8, fontFamily: 'var(--font-body)', letterSpacing: '0.01em' }}>{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 var(--gutter)' }}>
          <SectionHead eyebrow="Open Positions" title="Roles we're hiring for now." />
          {jobs.length > 0 ? (
            <div style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {jobs.map(([role, dept, loc, type], i) => (
                <Reveal key={role} variant="up" delay={i * 0.04}>
                  <JobRow role={role} dept={dept} loc={loc} type={type} onApply={() => onNavigate('Contact')} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div style={{ marginTop: 36, padding: '36px 28px', background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)', textAlign: 'center' }}>
              <Icon name="briefcase" style={{ width: 40, height: 40, color: 'var(--text-muted)', marginBottom: 14 }} />
              <p style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h4)', color: 'var(--text-strong)', margin: '0 0 8px' }}>No open positions right now</p>
              <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: '0 0 20px' }}>We're always interested in exceptional talent. Send us your profile and we'll be in touch when the right opportunity arises.</p>
              <Button variant="primary" size="md" iconRight={<Icon name="arrow-right" style={{ width: 16, height: 16 }} />} onClick={() => onNavigate('Contact')}>Send Your Profile</Button>
            </div>
          )}
        </div>
      </section>
      <CTASection onNavigate={onNavigate} />
    </div>
  );
}

export function JobRow({ role, dept, loc, type, onApply }) {
  const [h, setH] = React.useState(false);
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', padding: '20px 24px',
      background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)',
      boxShadow: h ? 'var(--shadow-md)' : 'var(--shadow-xs)', transform: h ? 'translateX(4px)' : 'none', transition: 'all var(--dur-fast)',
    }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 17, color: 'var(--text-strong)', margin: '0 0 8px' }}>{role}</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Badge tone="blue" soft>{dept}</Badge>
          <Badge tone="neutral" soft>{loc}</Badge>
          <Badge tone="green" soft>{type}</Badge>
        </div>
      </div>
      <Button variant="outline" size="sm" iconRight={<Icon name="arrow-right" style={{ width: 16, height: 16 }} />} onClick={onApply}>Apply Now</Button>
    </div>
  );
}

export function ClientPortalPage({ onNavigate }) {
  const [tab, setTab] = React.useState('login');
  const tabs = [['login', 'Login'], ['register', 'Register'], ['forgot', 'Forgot Password']];
  return (
    <div style={{ minHeight: '100vh', display: 'grid' }} className="adr-portal" data-screen-label="Client Portal">
      <div style={{ position: 'relative', overflow: 'hidden', background: 'var(--grad-hero)', color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 'clamp(2rem,4vw,4rem)' }} className="adr-portal-brand">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(' + IMG('hero-automation') + ')', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.3 }} />
        <div style={{ position: 'relative' }}>
          <img src="/adirath-logo.webp" alt="ADIRATH" style={{ height: 42 }} />
        </div>
        <div style={{ position: 'relative' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-h1)', color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1, margin: '0 0 16px', maxWidth: 420 }}>Your projects, monitored in real time.</h2>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 16, maxWidth: 400, margin: 0 }}>Track BESS performance, service tickets, documentation and energy analytics from one secure dashboard.</p>
          <div style={{ display: 'flex', gap: 22, marginTop: 32 }}>
            {[['activity', 'Live telemetry'], ['file-text', 'Documents'], ['life-buoy', 'Support']].map(([ic, t]) => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.85)', fontSize: 13.5, fontWeight: 600 }}><Icon name={ic} style={{ width: 16, height: 16, color: 'var(--energy-bright)' }} />{t}</span>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>© 2026 ADIRATH · Client Portal</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(2rem,4vw,4rem)', background: 'var(--surface-page)' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h2)', color: 'var(--text-strong)', margin: '0 0 8px' }}>Client Portal</h1>
          <p style={{ color: 'var(--text-muted)', margin: '0 0 28px' }}>Secure access for ADIRATH clients & partners.</p>

          <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-pill)', marginBottom: 28 }}>
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} style={{
                flex: 1, padding: '9px 8px', borderRadius: 'var(--radius-pill)', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12.5,
                background: tab === k ? 'var(--surface-card)' : 'transparent', color: tab === k ? 'var(--accent)' : 'var(--text-muted)',
                boxShadow: tab === k ? 'var(--shadow-xs)' : 'none', transition: 'all var(--dur-fast)',
              }}>{l}</button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {tab === 'register' && <Input label="Full Name" icon={<Icon name="user" style={{ width: 18, height: 18 }} />} placeholder="Your name" />}
            {tab === 'register' && <Input label="Company" icon={<Icon name="building-2" style={{ width: 18, height: 18 }} />} placeholder="Company" />}
            <Input label="Email" type="email" icon={<Icon name="mail" style={{ width: 18, height: 18 }} />} placeholder="you@company.com" />
            {tab !== 'forgot' && <Input label="Password" type="password" icon={<Icon name="lock" style={{ width: 18, height: 18 }} />} placeholder="••••••••" />}
            {tab === 'login' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', cursor: 'pointer' }}><input type="checkbox" /> Remember me</label>
                <a onClick={() => setTab('forgot')} style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>Forgot password?</a>
              </div>
            )}
            <Button variant="primary" size="lg" full iconRight={<Icon name="arrow-right" style={{ width: 18, height: 18 }} />}>
              {tab === 'login' ? 'Sign In' : tab === 'register' ? 'Create Account' : 'Send Reset Link'}
            </Button>
            <div style={{ textAlign: 'center', fontSize: 13.5, color: 'var(--text-muted)' }}>
              Need help? <a onClick={() => onNavigate('Contact')} style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>Contact support</a>
            </div>
            <a onClick={() => onNavigate('Home')} style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}><Icon name="arrow-left" style={{ width: 14, height: 14 }} /> Back to website</a>
          </div>
        </div>
      </div>
    </div>
  );
}
